import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DISCIPLINES,
  CHALLENGES_PER_LEVEL,
  MAX_STARS_PER_CHALLENGE,
  getBadgeInfo,
  type DisciplineSlug,
  type Challenge,
} from "@jumpbean/shared";
import {
  Button,
  ProgressBar,
  LevelCompleteSummary,
  BadgeEarnedModal,
  CharacterImage,
  GLOW_COLORS,
} from "../components";
import { useGameStore } from "../stores/gameStore";
import { useAppStore } from "../stores/appStore";
import { apiGet } from "../lib/api";

interface ChallengeData {
  id: string;
  challengeNumber: number;
  type: string;
  content: Record<string, unknown>;
}

interface LevelData {
  id: string;
  levelNumber: number;
  name: string;
  badgeName: string;
  badgeImageUrl?: string;
}

// Themed backgrounds for each discipline
const DISCIPLINE_BG: Record<DisciplineSlug, string> = {
  math: "from-[#1a1a2e] via-[#16213e] to-[#0f0f23]",
  spelling: "from-[#0f2f1f] via-[#1a3f2a] to-[#0a2015]",
  patterns: "from-[#1f1a2f] via-[#2a1f3f] to-[#150f25]",
  typing: "from-[#1a1a3e] via-[#16164e] to-[#0f0f33]",
  programming: "from-[#2f2015] via-[#3f2a1a] to-[#25150a]",
};

// Character expressions for different states
const EXPRESSIONS: Record<string, "happy" | "encouraging" | "thinking" | "pointing"> = {
  notStarted: "pointing",
  inProgress: "encouraging",
  almostDone: "happy",
  complete: "happy",
};

/**
 * LevelPage - Challenge list for a specific level with character guide.
 *
 * Features:
 * - Character guide showing progress-appropriate expression
 * - Themed background matching discipline
 * - Challenge tiles in a grid layout
 * - Progress tracker and star counter
 * - Level completion celebration
 * - Badge earned modal
 */
export function LevelPage() {
  const navigate = useNavigate();
  const { discipline, level } = useParams<{
    discipline: DisciplineSlug;
    level: string;
  }>();

  const {
    currentChild,
    isLevelComplete,
    showBadgeModal,
    earnedBadge,
    sessionProgress,
    cachedProgress,
    startLevel,
    hideBadgeModal,
    loadProgress,
  } = useGameStore();

  const selectedChildId = useAppStore((state) => state.selectedChildId);

  const [challenges, setChallenges] = useState<ChallengeData[]>([]);
  const [levelData, setLevelData] = useState<LevelData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLevelComplete, setShowLevelComplete] = useState(false);

  const levelNumber = parseInt(level || "1", 10);
  const disciplineData = discipline ? DISCIPLINES[discipline] : null;

  // Get the child ID from either gameStore or appStore
  const childId = currentChild?.id || selectedChildId;
  const glowColor = discipline ? GLOW_COLORS[discipline] : "#FFD93D";
  const bgGradient = discipline ? DISCIPLINE_BG[discipline] : DISCIPLINE_BG.math;

  // Redirect to profiles if no child selected
  useEffect(() => {
    if (!childId) {
      navigate("/profiles");
    }
  }, [childId, navigate]);

  // Load challenges and level data
  useEffect(() => {
    if (!discipline || !levelNumber || !childId) return;

    const loadData = async () => {
      setIsLoading(true);

      try {
        // Always load progress from API to get latest data
        await loadProgress(childId);

        // Fetch level data
        const levelResponse = await apiGet<LevelData>(
          `/api/content/disciplines/${discipline}/levels/${levelNumber}`
        );

        if (levelResponse.data) {
          setLevelData(levelResponse.data);
        }

        // Fetch challenges for this level
        const challengesResponse = await apiGet<{
          levelId: string;
          levelNumber: number;
          challenges: ChallengeData[];
          totalChallenges: number;
        }>(`/api/content/disciplines/${discipline}/levels/${levelNumber}/challenges`);

        if (challengesResponse.data?.challenges) {
          setChallenges(challengesResponse.data.challenges);
        }
      } catch (error) {
        console.error("Failed to load level data:", error);
        // Use fallback data for demo
        setChallenges(
          Array.from({ length: CHALLENGES_PER_LEVEL }, (_, i) => ({
            id: `${discipline}-${levelNumber}-${i + 1}`,
            challengeNumber: i + 1,
            type: "counting",
            content: {},
          }))
        );
        setLevelData({
          id: `level-${discipline}-${levelNumber}`,
          levelNumber,
          name: getBadgeInfo(discipline, levelNumber)?.name ?? `Level ${levelNumber}`,
          badgeName:
            getBadgeInfo(discipline, levelNumber)?.name ?? `Level ${levelNumber} Badge`,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [discipline, levelNumber, childId, loadProgress]);

  // Check if level is complete
  useEffect(() => {
    if (isLevelComplete) {
      setShowLevelComplete(true);
    }
  }, [isLevelComplete]);

  // Calculate progress
  const getChallengeProgress = useCallback(
    (challengeId: string) => {
      // Check session progress first
      const sessionEntry = sessionProgress.find((p) => p.challengeId === challengeId);
      if (sessionEntry) {
        return { completed: true, stars: sessionEntry.starsEarned };
      }

      // Check cached progress
      const cachedEntry = cachedProgress.get(challengeId);
      if (cachedEntry) {
        return { completed: true, stars: cachedEntry.starsEarned };
      }

      return { completed: false, stars: 0 };
    },
    [sessionProgress, cachedProgress]
  );

  const completedCount = challenges.filter(
    (c) => getChallengeProgress(c.id).completed
  ).length;

  const totalStars = challenges.reduce(
    (sum, c) => sum + getChallengeProgress(c.id).stars,
    0
  );

  const progress = (completedCount / challenges.length) * 100;
  const isComplete = completedCount >= challenges.length;

  // Determine character expression based on progress
  const getCharacterExpression = () => {
    if (isComplete) return EXPRESSIONS.complete;
    if (progress >= 70) return EXPRESSIONS.almostDone;
    if (progress > 0) return EXPRESSIONS.inProgress;
    return EXPRESSIONS.notStarted;
  };

  // Find the next uncompleted challenge
  const nextChallenge = challenges.find(
    (c) => !getChallengeProgress(c.id).completed
  );

  const handleStartChallenge = useCallback(
    (challenge: ChallengeData) => {
      // Initialize level in game store if needed
      if (levelData && challenges.length > 0 && discipline) {
        startLevel(
          {
            id: levelData.id,
            disciplineId: discipline,
            levelNumber,
            name: levelData.name,
            description: "",
            badgeName: levelData.badgeName,
            badgeImageUrl: levelData.badgeImageUrl ?? "",
            isActive: true,
          },
          challenges.map((c) => ({
            id: c.id,
            levelId: levelData.id,
            challengeNumber: c.challengeNumber,
            type: c.type as Challenge["type"],
            content: c.content,
            difficulty: 1,
            isActive: true,
          }))
        );
      }

      navigate(`/challenge/${challenge.id}`, {
        state: {
          discipline,
          characterName: disciplineData?.characterName,
        },
      });
    },
    [levelData, challenges, startLevel, navigate, discipline, disciplineData, levelNumber]
  );

  const handleContinue = useCallback(() => {
    if (nextChallenge) {
      handleStartChallenge(nextChallenge);
    }
  }, [nextChallenge, handleStartChallenge]);

  const handleLevelCompleteContinue = useCallback(() => {
    setShowLevelComplete(false);
    // Go to next level or back to discipline page
    if (levelNumber < 5) {
      navigate(`/play/${discipline}/${levelNumber + 1}`);
    } else {
      navigate(`/play/${discipline}`);
    }
  }, [navigate, discipline, levelNumber]);

  const handleLevelCompleteReplay = useCallback(() => {
    setShowLevelComplete(false);
    // Reset and replay the level
    if (challenges.length > 0) {
      handleStartChallenge(challenges[0]!);
    }
  }, [challenges, handleStartChallenge]);

  const handleBadgeModalClose = useCallback(() => {
    hideBadgeModal();
    // Show level complete after badge modal
    if (isLevelComplete) {
      setShowLevelComplete(true);
    }
  }, [hideBadgeModal, isLevelComplete]);

  if (!disciplineData || !discipline || isNaN(levelNumber)) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4">
        <p className="font-body text-body-lg text-neutral-600">Level not found</p>
      </div>
    );
  }

  // Show level complete summary
  if (showLevelComplete && levelData) {
    return (
      <LevelCompleteSummary
        levelNumber={levelNumber}
        levelName={levelData.name}
        discipline={discipline}
        characterName={disciplineData.characterName}
        starsEarned={totalStars}
        maxStars={CHALLENGES_PER_LEVEL * MAX_STARS_PER_CHALLENGE}
        badgeName={earnedBadge?.name}
        badgeImageUrl={earnedBadge?.imageUrl}
        onContinue={handleLevelCompleteContinue}
        onReplay={handleLevelCompleteReplay}
        showReplay
      />
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b ${bgGradient}`}>
      {/* Background decorations */}
      <BackgroundDecorations discipline={discipline} />

      {/* Badge earned modal */}
      <BadgeEarnedModal
        isOpen={showBadgeModal}
        onClose={handleBadgeModalClose}
        discipline={discipline}
        level={levelNumber}
        continueText="See Results"
      />

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin w-16 h-16 border-4 border-white/20 border-t-sunshine-500 rounded-full mx-auto" />
            <p className="mt-4 font-display font-bold text-body-lg text-white/60">
              Loading challenges...
            </p>
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="relative z-10 px-4 py-8 max-w-lg mx-auto">
          {/* Character guide header */}
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <CharacterImage
                discipline={discipline}
                expression={getCharacterExpression()}
                size="xl"
                floating
                glowing
              />
              {/* Progress indicator around character */}
              {progress > 0 && (
                <div
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-white font-display font-bold text-body-sm"
                  style={{ background: glowColor }}
                >
                  {Math.round(progress)}%
                </div>
              )}
            </div>

            <h1
              className="mt-4 font-display font-extrabold text-display-md text-white"
              style={{ textShadow: `0 2px 8px ${glowColor}80` }}
            >
              {levelData?.name ?? `Level ${levelNumber}`}
            </h1>

            <p className="mt-2 font-body text-body-lg text-white/70">
              {disciplineData.name} with {disciplineData.characterName}
            </p>

            {/* Progress bar */}
            <div className="mt-6 max-w-xs mx-auto">
              <div
                className="h-3 bg-white/10 rounded-full overflow-hidden"
                style={{ boxShadow: `0 0 10px ${glowColor}30` }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${glowColor}, white)`,
                  }}
                />
              </div>
              <div className="mt-2 flex justify-between font-body text-body-sm text-white/60">
                <span>
                  {completedCount} of {challenges.length} complete
                </span>
                {totalStars > 0 && (
                  <span className="flex items-center gap-1">
                    {totalStars} <span className="text-sunshine-400">★</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Start/Continue button */}
          <div className="flex justify-center mb-8">
            <Button
              variant="primary"
              size="lg"
              onClick={handleContinue}
              disabled={isComplete}
              className="shadow-xl"
              style={{
                boxShadow: `0 4px 20px ${glowColor}50`,
              }}
            >
              {completedCount === 0
                ? "Start Learning!"
                : isComplete
                ? "Level Complete!"
                : "Continue Playing"}
            </Button>
          </div>

          {/* Challenge grid */}
          <div className="grid grid-cols-5 gap-3">
            {challenges.map((challenge) => {
              const { completed, stars } = getChallengeProgress(challenge.id);
              const isNext = !completed && challenge.id === nextChallenge?.id;
              const canAccess = completed || isNext;

              return (
                <button
                  key={challenge.id}
                  onClick={() => canAccess && handleStartChallenge(challenge)}
                  disabled={!canAccess}
                  className="focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50 rounded-xl disabled:cursor-not-allowed"
                >
                  <div
                    className={`
                      aspect-square rounded-xl flex flex-col items-center justify-center
                      transition-all duration-300
                      ${
                        completed
                          ? "bg-white/20 border-2 border-white/30"
                          : isNext
                          ? "bg-white/10 border-2 border-sunshine-400 animate-pulse"
                          : "bg-white/5 border-2 border-white/10 opacity-50"
                      }
                      ${canAccess ? "hover:scale-105 active:scale-95" : ""}
                    `}
                    style={{
                      boxShadow: completed
                        ? `0 0 15px ${glowColor}30`
                        : isNext
                        ? `0 0 20px rgba(255,217,61,0.4)`
                        : undefined,
                    }}
                  >
                    <span
                      className={`font-display font-bold text-lg ${
                        completed
                          ? "text-white"
                          : isNext
                          ? "text-sunshine-400"
                          : "text-white/40"
                      }`}
                    >
                      {challenge.challengeNumber}
                    </span>
                    {completed && (
                      <div className="flex gap-0.5 mt-1">
                        {Array.from({ length: stars }).map((_, i) => (
                          <span
                            key={i}
                            className="text-xs text-sunshine-400"
                            style={{ textShadow: "0 0 4px rgba(255,217,61,0.5)" }}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    )}
                    {isNext && (
                      <span className="text-[10px] text-sunshine-400 mt-0.5">
                        NEXT
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Character encouragement message */}
          <div
            className="mt-8 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
            style={{ boxShadow: `0 0 20px ${glowColor}10` }}
          >
            <p className="font-body text-body-lg text-white/80 text-center">
              {getEncouragementMessage(disciplineData.characterName, progress, isComplete)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Get encouragement message based on progress
 */
function getEncouragementMessage(
  characterName: string,
  progress: number,
  isComplete: boolean
): string {
  if (isComplete) {
    return `Amazing job! ${characterName} is so proud of you!`;
  }
  if (progress >= 70) {
    return `You're almost there! ${characterName} believes in you!`;
  }
  if (progress >= 30) {
    return `Great progress! Keep going with ${characterName}!`;
  }
  if (progress > 0) {
    return `Nice start! ${characterName} is cheering for you!`;
  }
  return `Let's learn together! Tap a challenge to begin!`;
}

/**
 * Background decorations for each discipline
 */
function BackgroundDecorations({ discipline }: { discipline: DisciplineSlug }) {
  const colors = getDecorationColors(discipline);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${4 + Math.random() * 8}px`,
            height: `${4 + Math.random() * 8}px`,
            background: colors[i % colors.length],
            opacity: 0.15 + Math.random() * 0.25,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${4 + Math.random() * 3}s`,
          }}
        />
      ))}
      {/* Stars */}
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute rounded-full bg-white"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
            opacity: 0.2 + Math.random() * 0.4,
            animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

function getDecorationColors(discipline: DisciplineSlug): string[] {
  const colorSets: Record<DisciplineSlug, string[]> = {
    math: ["#E8A0FF", "#F0C4FF", "#FFD93D", "#C77DDB"],
    spelling: ["#90EE90", "#B8F5C8", "#FFB8D0", "#6BCB77"],
    patterns: ["#FF6B6B", "#FFB347", "#FFE66D", "#98D89B", "#74B9FF", "#A29BFE"],
    typing: ["#4A90D9", "#7EB3E8", "#FFD700", "#3A75B0"],
    programming: ["#FF8C42", "#00D4FF", "#E8C4A0", "#B87333"],
  };
  return colorSets[discipline];
}

export default LevelPage;
